import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Package, X, Check } from "lucide-react";

const CATEGORIES = ["beer", "liquor", "cocktail_mix", "wine", "food_ingredient", "supplies", "other"];
const BASE_UNITS = ["ml", "L", "g", "kg", "piece"];

export default function InventoryItemsPage() {
  const { hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);

  const load = async () => {
    try {
      const { data } = await api.get("/inventory/items");
      setItems(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const patch = {
          name: form.name,
          category: form.category,
          low_stock_threshold: parseFloat(form.low_stock_threshold) || 0,
          current_cost: parseFloat(form.current_cost) || 0,
          notes: form.notes,
        };
        await api.patch(`/inventory/items/${editing.id}`, patch);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", {
          ...form,
          purchase_to_base_ratio: parseFloat(form.purchase_to_base_ratio) || 1,
          low_stock_threshold: parseFloat(form.low_stock_threshold) || 0,
          current_cost: parseFloat(form.current_cost) || 0,
        });
        toast.success("Item created");
      }
      setCreating(false); setEditing(null); setForm(BLANK_FORM); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name, code: it.code, category: it.category,
      base_unit: it.base_unit, purchase_unit: it.purchase_unit,
      purchase_to_base_ratio: it.purchase_to_base_ratio,
      low_stock_threshold: it.low_stock_threshold,
      current_cost: it.current_cost, notes: it.notes || "",
    });
    setCreating(true);
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/inventory/items/${it.id}`, { active: !it.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-items-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Master file</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Items</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Every item is tracked in a canonical base unit. Purchases and wastage are converted server-side so the ledger stays consistent regardless of the unit used by staff.
          </p>
        </div>
        {hasPerm("inventory.adjust") && (
          <button onClick={() => { setEditing(null); setForm(BLANK_FORM); setCreating(true); }}
                  data-testid="inv-new-item-btn"
                  className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> New item
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="mt-10 flex flex-wrap items-center gap-2 animate-fade-up animate-fade-up-1">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`inv-cat-${c}`}
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    filter === c ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>
            {c.replace("_"," ")}
            <span className="ml-1 mono opacity-70">{c === "all" ? items.length : items.filter((i)=>i.category===c).length}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-10 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="inv-items-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-4">Item</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Base unit</div>
          <div className="col-span-2">Purchase → base</div>
          <div className="col-span-1 text-right">Cost</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {filtered.length === 0 && (
          <div className="py-6 ink-subtle text-center text-sm">No items in this category.</div>
        )}
        {filtered.map((it) => (
          <button key={it.id}
                  onClick={() => openEdit(it)}
                  data-testid={`inv-item-${it.id}`}
                  className="w-full text-left grid grid-cols-12 py-3 items-center hover:bg-foreground/[0.03] transition-colors">
            <div className="col-span-4 pr-4">
              <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{it.name}</div>
              <div className="text-xs ink-subtle mono mt-0.5">{it.code}</div>
            </div>
            <div className="col-span-2 tiny-label">{it.category.replace("_"," ")}</div>
            <div className="col-span-2 mono text-sm ink">{it.base_unit}</div>
            <div className="col-span-2 mono text-sm ink-muted">1 {it.purchase_unit} = {it.purchase_to_base_ratio} {it.base_unit}</div>
            <div className="col-span-1 text-right mono text-sm ink">₱{it.current_cost}</div>
            <div className="col-span-1 text-right">
              {it.active
                ? <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-foreground">ACTIVE</span>
                : <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-red-500">OFF</span>}
            </div>
          </button>
        ))}
      </div>

      {/* Drawer */}
      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inv-item-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setCreating(false); setEditing(null); }}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Inventory</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing ? "Edit item" : "New item"}</h2>
              </div>
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Name">
              <input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="inv-form-name"/>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Code (unique)">
                <input required disabled={!!editing} value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} className="i mono" data-testid="inv-form-code"/>
              </F>
              <F label="Category">
                <select required value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} className="i" data-testid="inv-form-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_"," ")}</option>)}
                </select>
              </F>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <F label="Base unit">
                <select disabled={!!editing} required value={form.base_unit} onChange={(e)=>setForm({...form, base_unit: e.target.value})} className="i" data-testid="inv-form-base-unit">
                  {BASE_UNITS.map((u) => <option key={u}>{u}</option>)}
                </select>
              </F>
              <F label="Purchase unit">
                <input disabled={!!editing} required value={form.purchase_unit} onChange={(e)=>setForm({...form, purchase_unit: e.target.value})} className="i" data-testid="inv-form-purchase-unit" placeholder="e.g. bottle-750ml"/>
              </F>
            </div>

            <F label={`1 ${form.purchase_unit || "purchase unit"} equals how many ${form.base_unit || "base units"}?`}>
              <input disabled={!!editing} required type="number" step="0.01" value={form.purchase_to_base_ratio} onChange={(e)=>setForm({...form, purchase_to_base_ratio: e.target.value})} className="i mono" data-testid="inv-form-ratio"/>
            </F>

            <div className="grid grid-cols-2 gap-3">
              <F label={`Low-stock threshold (${form.base_unit})`}>
                <input type="number" step="0.01" value={form.low_stock_threshold} onChange={(e)=>setForm({...form, low_stock_threshold: e.target.value})} className="i mono" data-testid="inv-form-low"/>
              </F>
              <F label={`Current cost per ${form.base_unit}`}>
                <input type="number" step="0.01" value={form.current_cost} onChange={(e)=>setForm({...form, current_cost: e.target.value})} className="i mono" data-testid="inv-form-cost"/>
              </F>
            </div>

            <F label="Notes">
              <textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i"/>
            </F>

            {editing && (
              <div className="mt-6">
                <button type="button" onClick={() => { toggleActive(editing); setCreating(false); setEditing(null); }}
                        className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-500 hover:underline">
                  {editing.active ? "Deactivate item" : "Reactivate item"}
                </button>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="inv-form-submit" className="btn-pill btn-pill-solid h-11 flex-1">
                <Check size={13}/> {editing ? "Save changes" : "Create item"}
              </button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }
                     .i:disabled { opacity: 0.55; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK_FORM = {
  name: "", code: "", category: "liquor",
  base_unit: "ml", purchase_unit: "bottle-750ml",
  purchase_to_base_ratio: 750, low_stock_threshold: 0,
  current_cost: 0, notes: "",
};

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
